/**
 * System status API — contract #23 (docs/05-dev-spec.md §4, task T.C5 / И8).
 *
 *   #23 GET /api/system/status → `{ data: SystemStatusDto }`   (VIEWER and up)
 *
 * CONTRACT-FIRST: T.B5 (the admin module) lands in parallel, so the DTOs below
 * are mirrored 1:1 from the generated `packages/shared/src/openapi/api-types.ts`
 * (`SystemStatusDto`, `MailStatusDto`, `UntiStatusDto`,
 * `SourceSnapshotHealthDto`, `SnapshotSourcesHealthDto`). Every field of #23 is
 * OPTIONAL on the wire — the contract types `db`/`redis`/`mail`/… that way,
 * because "a failing dependency degrades the payload without failing the
 * request" — so the panel reads absence as «нет данных», never as a crash.
 *
 * TWO ADDITIVE fields are read tolerantly (absent → no badge / «нет данных»),
 * both derived from tables that already exist:
 *   - `matcher` — the `SyncState['MATCHER']` projection D2 writes (same shape as
 *     `unti`; a traffic light without it would leave the matching pipeline dark).
 *   - `pendingApplies` — the count of `sync_state` rows `SNAPSHOT_APPLY_REQUEST:*`
 *     that T.B4 enqueues for the worker to drain. Zero / absent is normal, > 0
 *     is the «manual upload is waiting to be applied» warning badge.
 *
 * The tolerant unwrap lives in `pages/settings/settings-model.ts`
 * (`parseSystemStatus`) — this module value-imports `client.ts`
 * (`import.meta.env`), which the plain-Node `unit` project cannot load. The same
 * split puts `parseSnapshotList` next to the snapshots page.
 */

import { useQuery } from '@tanstack/react-query';

import { API_BASE_URL, apiRequest, parseErrorBody } from './client';

/** `MailStatusDto` — the mail-worker ledger plus the two `mail_item` counters. */
export interface MailStatusDto {
  lastRunAt: string | null;
  lastSuccessAt: string | null;
  /** Count of `mail_item.status = 'PENDING'` — letters not yet processed. */
  pending: number;
  /** `mail_item` rows with status FAILED (dead-lettered letters). */
  failedItems: number;
}

/**
 * `UntiStatusDto` — a `SyncState` projection. Used for the unti (FTP) sync AND,
 * additively, for the venue matcher, which writes the same shape under the
 * `MATCHER` key.
 */
export interface SyncStatusDto {
  lastSyncAt: string | null;
  lastSuccessAt: string | null;
  /** Applications seen by the last successful sync (null = never succeeded). */
  lastCount: number | null;
  /** Machine code of the last failure (null = healthy). */
  errorCode?: string | null;
}

/** `SourceSnapshotHealthDto` — the freshest evidence of one snapshot source. */
export interface SourceSnapshotHealthDto {
  lastSnapshotAt: string | null;
}

/**
 * ADDITIVE (v1.9.22, T.Z106) — `ContractSyncStatusDto`, the Settings section
 * «Синк договоров»: the health of the T.Z101 contract-status sync plus the three
 * LIVE fact counts off `applications`.
 *
 * The api holds NO `FUTURE_DB_DSN` — only the worker's contract-sync job does —
 * so «connection status» here is the sync's LAST RUN state off the
 * `sync_state` row `CONTRACT_SYNC` the worker writes every 5 min, folded by the
 * api into `state`: `OK` (a fresh pass, no error) / `FAIL` (an `errorCode` on
 * record, or a pulse gone past two intervals) / `NEVER` (no row). The api
 * computes the fold, so the panel trusts it and never re-derives freshness from
 * `lastRunAt` — one source of truth for the verdict.
 */
export type ContractSyncState = 'OK' | 'FAIL' | 'NEVER';

export interface ContractSyncStatusDto {
  /** Start of the last pass (null = never ran). */
  lastRunAt: string | null;
  /** The api's fold of the ledger row. */
  state: ContractSyncState;
  /** The failed pass's own message (null = no failure on record). */
  errorCode: string | null;
  /** Applications the last pass examined (null = never ran). */
  lastCount: number | null;
  /** LIVE — `contract_signed IS TRUE` («Подписали»). */
  signed: number;
  /** LIVE — `contract_arrived IS TRUE AND NOT contract_signed` («Не подписали»). */
  arrivedUnsigned: number;
  /** LIVE — `contract_not_arrived IS TRUE` («Не пришли»). */
  notArrived: number;
  /** ADDITIVE (v1.9.24, T.Z111) — LIVE — `enroll_accepted IS TRUE` («Зачислен»). */
  enrolled?: number;
  /** ADDITIVE (v1.9.24, T.Z111) — LIVE — `enroll_declined IS TRUE` («Отказано в зачислении»). */
  declined?: number;
}

/** `SnapshotSourcesHealthDto` of #23 (ЕПГУ/ЕВИ; FTP freshness is `unti` here). */
export interface SnapshotSourcesDto {
  EPGU: SourceSnapshotHealthDto;
  EVI: SourceSnapshotHealthDto;
}

/**
 * ADDITIVE (v1.9.38, T.U1) — `Unti2035StatusDto`, the Settings section «Синк
 * УНТИ (API)»: the cat.2035.university sweep's last-run state (the api's own
 * fold, never re-derived here) plus the two LIVE counts.
 */
export type Unti2035SyncState = 'OK' | 'FAIL' | 'NEVER';

export interface Unti2035StatusDto {
  /** Start of the last sweep (null = never ran). */
  lastRunAt: string | null;
  /** The api's fold of the ledger row. */
  state: Unti2035SyncState;
  /** The failed sweep's own code (null = healthy). */
  errorCode: string | null;
  /** Mirror rows the last sweep fetched (null = never ran). */
  lastCount: number | null;
  /** LIVE — the mirror's size (`unti_applications`). */
  mirrorTotal: number;
  /** LIVE — `applications.unti_declined_at IS NOT NULL` («Отказов по УНТИ (API)»). */
  applicationsDeclined: number;
}

/** The #23 payload — the «как у нас дела» panel. */
export interface SystemStatusDto {
  /** Live reachability probes. */
  db?: boolean;
  redis?: boolean;
  mail?: MailStatusDto;
  unti?: SyncStatusDto;
  /** Additive: the `SyncState['MATCHER']` projection (see the module comment). */
  matcher?: SyncStatusDto;
  /**
   * ADDITIVE (v1.9.22, T.Z106) — the contract-sync health + the live fact counts.
   * OPTIONAL on the wire (a degraded database omits the block); absence reads
   * grey «нет данных», never a crash.
   */
  contractSync?: ContractSyncStatusDto;
  /**
   * ADDITIVE (v1.9.38, T.U1) — the cat.2035.university sweep's health + the
   * mirror/decline counts. OPTIONAL on the wire (a degraded database, an older
   * api); absence reads grey «нет данных», never a crash.
   */
  unti2035?: Unti2035StatusDto;
  snapshots?: SnapshotSourcesDto;
  /** Deployed build version. */
  version?: string;
  /** Process uptime in seconds. */
  uptime?: number;
  /** Additive: pending `SNAPSHOT_APPLY_REQUEST:*` rows (see the module comment). */
  pendingApplies?: number;
  /**
   * ADDITIVE v1.8.6 (T.Z40) — how many recent snapshots MISSED their apply window
   * (no ledger row, clean ingest, past `SNAPSHOT_APPLY_STALE_MS`, inside the 24 h
   * freshness horizon). The escalation of `pendingApplies`: queued is yellow,
   * stalled is danger.
   */
  stalledApplies?: number;
}

export const SYSTEM_STATUS_PATH = '/system/status';

/** И8 refresh cadence (docs/06-tasks.md T.C5: "polls #23 with 30s refresh"). */
export const SYSTEM_STATUS_REFRESH_MS = 30_000;

/**
 * ADDITIVE (v1.9.38, T.U1) — contract #35, the «Обновить сейчас» button of the
 * «Синк УНТИ (API)» row. The call ARMS an out-of-band sweep (202
 * `{data:{queued,runId}}`; the worker's drain poll sweeps within ~30 s) and a
 * FRESH pending request answers **409 UNTI_SYNC_RUN_PENDING** — «уже идёт»,
 * which the row renders as its own message, never as a red error.
 */
export const UNTI_SYNC_RUN_PATH = '/system/unti-sync/run';

export interface UntiSyncRunDto {
  queued: boolean;
  runId: number;
}

export class UntiSyncRunError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    message: string,
  ) {
    super(message);
    this.name = 'UntiSyncRunError';
  }
}

export async function requestUntiSyncRun(signal?: AbortSignal): Promise<UntiSyncRunDto> {
  const response = await fetch(`${API_BASE_URL}${UNTI_SYNC_RUN_PATH}`, { method: 'POST', signal });

  if (!response.ok) {
    let body: unknown = null;
    try {
      body = await response.json();
    } catch {
      // Non-JSON — `parseErrorBody` falls back to the status-derived code.
    }
    const { code, message } = parseErrorBody(body, response.status);
    throw new UntiSyncRunError(response.status, code, message);
  }

  const payload = (await response.json()) as Record<string, unknown>;
  const data = payload['data'];
  // A body without the envelope still reads as "queued" — an older api stub
  // must not turn a 202 into a rendered error.
  return data && typeof data === 'object'
    ? (data as UntiSyncRunDto)
    : { queued: true, runId: Number(payload['runId'] ?? 0) };
}

/**
 * #23, RAW. Deliberately un-normalised: the unwrap is pure logic and lives in
 * `pages/settings/settings-model.ts`, where the unit project can reach it.
 */
export async function fetchSystemStatus(signal?: AbortSignal): Promise<unknown> {
  // `apiRequest` (not a bare fetch) so a dead session behaves like everywhere
  // else in the app: a 401 is a full-page hop to the OAuth entry point.
  return apiRequest<unknown>('GET', SYSTEM_STATUS_PATH, { signal });
}

const SYSTEM_STATUS_ROOT = ['system', 'status'] as const;

/**
 * #23 — the panel's only source. Polls on a fixed 30s interval, does NOT
 * refetch on focus (a returning tab must not stampede a struggling api), and
 * never retries: the next tick is one interval away anyway.
 */
export function useSystemStatus() {
  return useQuery({
    queryKey: SYSTEM_STATUS_ROOT,
    queryFn: ({ signal }) => fetchSystemStatus(signal),
    refetchInterval: SYSTEM_STATUS_REFRESH_MS,
    refetchOnWindowFocus: false,
    staleTime: SYSTEM_STATUS_REFRESH_MS - 10_000,
    retry: 0,
  });
}
